import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import cn from "classnames";
import { useIntl } from "react-intl";

import DuplicateStatus from "@mapmycustomers/shared/enum/DuplicateStatus";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { DuplicatePair } from "@mapmycustomers/shared/types/entity/Duplicate";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import EntityPriority from "@app/scene/settings/component/MergeDuplicates/enum/EntityPriority";
import {
  getDuplicates,
  getSelectedAsPrioritizedEntity,
  getSelectedPair,
  getSelectedPairIndex,
} from "@app/scene/settings/component/MergeDuplicates/store";
import {
  changeDuplicatePairDismission,
  mergeDuplicates,
  selectDuplicatePair,
} from "@app/scene/settings/component/MergeDuplicates/store/actions";
import { dataMergingAnalytics } from "@app/scene/settings/settingsAnaytics";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./Navigation.module.scss";

interface Props {
  changeDuplicatePairDismission: typeof changeDuplicatePairDismission.request;
  entityType: EntityType;
  isNextAvailable: boolean;
  isPreviousAvailable: boolean;
  mergeDuplicates: typeof mergeDuplicates;
  priority: EntityPriority;
  selectDuplicatePair: typeof selectDuplicatePair.request;
  selectedIndex: number;
  selectedPair: DuplicatePair;
}

const Navigation: React.FC<Props> = ({
  changeDuplicatePairDismission,
  entityType,
  isNextAvailable,
  isPreviousAvailable,
  mergeDuplicates,
  priority,
  selectDuplicatePair,
  selectedIndex,
  selectedPair,
}) => {
  const intl = useIntl();

  const handleToggleDismiss = useCallback(() => {
    dataMergingAnalytics?.clicked([
      getEntityTypeDisplayName(intl, entityType, {
        lowercase: false,
        plural: true,
      }),
      "Review & Merge",
      selectedPair.status === DuplicateStatus.DISMISSED ? "Undo dismission" : "Dismiss",
    ]);

    changeDuplicatePairDismission({
      dismissed: !(selectedPair.status === DuplicateStatus.DISMISSED),
      pair: selectedPair,
    });
  }, [changeDuplicatePairDismission, entityType, intl, selectedPair]);

  return (
    <div className={styles.container}>
      <Row gutter={layout.spacerM} justify="end">
        <Col>
          <Button onClick={handleToggleDismiss}>
            {selectedPair.status === DuplicateStatus.DISMISSED
              ? intl.formatMessage({
                  id: "settings.mergeDuplicates.previewMergePane.navigation.undoDismission",
                  defaultMessage: "Undo dismission",
                  description:
                    "Settings Merge duplicates - preview/merge pane - navigation - Undo dismission",
                })
              : intl.formatMessage({
                  id: "settings.mergeDuplicates.previewMergePane.navigation.dismiss",
                  defaultMessage: "Dismiss",
                  description:
                    "Settings Merge duplicates - preview/merge pane - navigation - dismiss",
                })}
          </Button>
        </Col>
        <Col>
          <Popconfirm
            cancelText={intl.formatMessage({
              id: "settings.mergeDuplicates.previewMergePane.navigation.confirmation.cancel",
              defaultMessage: "Wait",
              description:
                "Settings Merge duplicates - preview/merge pane - navigation - Confirmation cancel",
            })}
            description={intl.formatMessage(
              {
                id: "settings.mergeDuplicates.previewMergePane.navigation.confirmation.title.paragraph2",
                defaultMessage:
                  "If you are unsure, review the <b>“What happens when I merge records?”</b> section before merging.",
                description:
                  "Settings Merge duplicates - preview/merge pane - navigation - Confirmation paragraph 2",
              },
              { b: (text) => <b>{text}</b> }
            )}
            disabled={priority === EntityPriority.NONE}
            okText={intl.formatMessage({
              id: "settings.mergeDuplicates.previewMergePane.navigation.confirmation.confirm",
              defaultMessage: "Proceed with merge",
              description:
                "Settings Merge duplicates - preview/merge pane - navigation - Confirmation action",
            })}
            onConfirm={() => mergeDuplicates()}
            placement="rightTop"
            title={intl.formatMessage({
              id: "settings.mergeDuplicates.previewMergePane.navigation.confirmation.title.paragraph1",
              defaultMessage:
                "Are you sure you want to merge these records? This action will delete the secondary record after merge and cannot be undone.",
              description:
                "Settings Merge duplicates - preview/merge pane - navigation - Confirmation paragraph 1",
            })}
          >
            <Button disabled={priority === EntityPriority.NONE} type="primary">
              {intl.formatMessage({
                id: "settings.mergeDuplicates.previewMergePane.navigation.merge",
                defaultMessage: "Merge Records",
                description:
                  "Settings Merge duplicates - preview/merge pane - navigation - Merge Records",
              })}
            </Button>
          </Popconfirm>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col>
          <div
            className={cn(styles.link, { [styles.disabledLink]: !isPreviousAvailable })}
            onClick={() => {
              dataMergingAnalytics?.clicked([
                getEntityTypeDisplayName(intl, entityType as EntityType, {
                  lowercase: false,
                  plural: true,
                }),
                "Review & Merge",
                "Previous duplicate",
              ]);
              selectDuplicatePair(selectedIndex - 1);
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            {intl.formatMessage({
              id: "settings.mergeDuplicates.previewMergePane.navigation.previous",
              defaultMessage: "Previous duplicate",
              description:
                "Settings Merge duplicates - preview/merge pane - navigation - Previous duplicate",
            })}
          </div>
        </Col>
        <Col>
          <div
            className={cn(styles.link, { [styles.disabledLink]: !isNextAvailable })}
            onClick={() => {
              dataMergingAnalytics?.clicked([
                getEntityTypeDisplayName(intl, entityType as EntityType, {
                  lowercase: false,
                  plural: true,
                }),
                "Review & Merge",
                "Next duplicate",
              ]);
              selectDuplicatePair(selectedIndex + 1);
            }}
          >
            {intl.formatMessage({
              id: "settings.mergeDuplicates.previewMergePane.navigation.next",
              defaultMessage: "Next duplicate",
              description:
                "Settings Merge duplicates - preview/merge pane - navigation - Next duplicate",
            })}
            <FontAwesomeIcon icon={faChevronRight} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isNextAvailable: (getSelectedPairIndex(state) ?? 0) < getDuplicates(state).length - 1,
  isPreviousAvailable: (getSelectedPairIndex(state) ?? 0) > 0,
  priority: getSelectedAsPrioritizedEntity(state),
  selectedIndex: getSelectedPairIndex(state) ?? 0,
  selectedPair: getSelectedPair(state)!,
});

const mapDispatchToProps = {
  changeDuplicatePairDismission: changeDuplicatePairDismission.request,
  mergeDuplicates,
  selectDuplicatePair: selectDuplicatePair.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Navigation);
